if (!customElements.get('br-lookbook')) {
  class BrLookbook extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-br-lookbook-track]');
      this.slides = [...this.querySelectorAll('[data-br-lookbook-slide]')];
      this.sortSlidesByRecordCode();
      this.prev = this.querySelector('[data-br-lookbook-prev]');
      this.next = this.querySelector('[data-br-lookbook-next]');
      this.pages = [...this.querySelectorAll('[data-br-lookbook-page]')];
      this.counter = this.querySelector('[data-br-lookbook-count]');
      this.mode = this.querySelector('[data-br-lookbook-mode]');
      this.modeCopy = this.querySelector('[data-br-lookbook-mode-copy]');
      this.focusLinks = [...this.querySelectorAll('[data-br-lookbook-focus]')];
      this.panels = [...this.querySelectorAll('[data-br-lookbook-panel]')];
      this.currency = this.dataset.currency || 'USD';
      this.index = 0;
      this.isFocused = false;
      this.suppressClick = false;
      this.transitioning = false;
      this.rebalancing = false;
      this.drag = null;
      this.raf = null;
      this.loopClones = [];
      this.railSlides = [...this.slides];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.currency,
      });

      if (!this.track || !this.slides.length) return;

      this.prev?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.pages.forEach((page, index) => page.addEventListener('click', () => this.goToLineup(index)));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.track.addEventListener('click', this.onTrackClick, true);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.buildLoop();
      this.goToLineup(0, false, true);
      requestAnimationFrame(() => this.renderLineupScale());
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.prev?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.track.removeEventListener('click', this.onTrackClick, true);
      window.removeEventListener('resize', this.onResize);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.flight?.remove();
    }

    onPrevious = () => {
      if (this.isFocused) this.navigateFocused(this.index - 1);
    };

    onNext = () => {
      if (this.isFocused) this.navigateFocused(this.index + 1);
    };

    onScroll = () => {
      if (this.isFocused || this.transitioning || this.rebalancing) return;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.rebalanceLoop();
        this.renderLineupScale();
        this.index = this.nearestIndex();
        this.renderNeutral();
      });
    };


    onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('button, input, summary')) return;
      this.drag = {
        x: event.clientX,
        scroll: this.track.scrollLeft,
        index: this.nearestIndex(),
        pointerId: event.pointerId,
        moved: false,
      };
    };

    onPointerMove = (event) => {
      if (!this.drag) return;
      const delta = event.clientX - this.drag.x;
      if (Math.abs(delta) > 4 && !this.drag.moved) {
        this.drag.moved = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(this.drag.pointerId);
      }
      this.track.scrollLeft = this.drag.scroll - delta;
    };

    onPointerUp = (event) => {
      if (!this.drag) return;
      const delta = event.clientX - this.drag.x;
      const target = Math.abs(delta) >= 28
        ? this.drag.index + (delta < 0 ? 1 : -1)
        : this.nearestIndex();
      this.suppressClick = this.drag.moved;
      this.drag = null;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      this.goToLineup(target);
      window.setTimeout(() => { this.suppressClick = false; }, 0);
    };

    onDragStart = (event) => {
      event.preventDefault();
    };

    onTrackClick = (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    };

    onResize = () => {
      this.goToLineup(this.index, false);
      requestAnimationFrame(() => this.renderLineupScale());
    };

    wrapIndex(index) {
      if (!this.slides.length) return 0;
      return ((index % this.slides.length) + this.slides.length) % this.slides.length;
    }

    sortSlidesByRecordCode() {
      if (!this.track || this.slides.length < 2) return;
      const indexed = this.slides.map((slide, index) => {
        const digits = slide.dataset.recordCode?.match(/\d+/)?.[0];
        const record = digits ? Number.parseInt(digits, 10) : Number.POSITIVE_INFINITY;
        return { slide, index, record };
      });
      indexed.sort((a, b) => (a.record - b.record) || (a.index - b.index));
      this.slides = indexed.map(({ slide }) => slide);
      this.slides.forEach((slide) => this.track.append(slide));
    }

    buildLoop() {
      this.slides.forEach((slide, index) => { slide.dataset.loopIndex = String(index); });
      if (this.slides.length < 2) {
        this.railSlides = [...this.slides];
        return;
      }

      const prefix = document.createDocumentFragment();
      const suffix = document.createDocumentFragment();
      this.prefixClones = this.slides.map((slide, index) => this.createLoopClone(slide, index, 'prefix'));
      this.suffixClones = this.slides.map((slide, index) => this.createLoopClone(slide, index, 'suffix'));
      this.prefixClones.forEach((clone) => prefix.append(clone));
      this.suffixClones.forEach((clone) => suffix.append(clone));
      this.track.prepend(prefix);
      this.track.append(suffix);
      this.loopClones = [...this.prefixClones, ...this.suffixClones];
      this.railSlides = [...this.track.querySelectorAll('.br-lookbook__slide')];
    }

    createLoopClone(slide, index, position) {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-br-lookbook-slide');
      clone.removeAttribute('aria-label');
      clone.setAttribute('data-br-lookbook-clone', position);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.loopIndex = String(index);
      clone.classList.remove('is-current', 'is-active');
      clone.querySelector('[data-br-lookbook-panel]')?.remove();
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.removeAttribute('fetchpriority');
      });

      const trigger = clone.querySelector('[data-br-lookbook-focus]');
      if (trigger) {
        trigger.removeAttribute('data-br-lookbook-focus');
        trigger.setAttribute('data-br-lookbook-loop-focus', '');
        trigger.setAttribute('tabindex', '-1');
        trigger.setAttribute('draggable', 'false');
      }
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      return clone;
    }

    getTrackPadding() {
      const styles = getComputedStyle(this.track);
      return parseFloat(styles.paddingInlineStart || styles.paddingLeft) || 0;
    }

    getNearestRailSlide(index) {
      const targetIndex = this.wrapIndex(index);
      const currentTarget = this.track.scrollLeft + (this.track.clientWidth / 2);
      return this.railSlides
        .filter((slide) => Number(slide.dataset.loopIndex) === targetIndex)
        .reduce((nearest, candidate) => {
          if (!nearest) return candidate;
          const candidateCenter = candidate.offsetLeft + (candidate.offsetWidth / 2);
          const nearestCenter = nearest.offsetLeft + (nearest.offsetWidth / 2);
          return Math.abs(candidateCenter - currentTarget) < Math.abs(nearestCenter - currentTarget)
            ? candidate
            : nearest;
        }, null);
    }

    rebalanceLoop() {
      if (!this.prefixClones?.length || !this.suffixClones?.length || this.rebalancing) return false;
      const padding = this.getTrackPadding();
      const originalStart = this.slides[0].offsetLeft;
      const suffixStart = this.suffixClones[0].offsetLeft;
      const cycleWidth = suffixStart - originalStart;
      if (cycleWidth <= 0) return false;

      const target = this.track.scrollLeft + padding;
      const buffer = cycleWidth * .5;
      let shift = 0;
      if (target < originalStart - buffer) shift = cycleWidth;
      if (target >= suffixStart + buffer) shift = -cycleWidth;
      if (!shift) return false;

      this.rebalancing = true;
      const previousBehavior = this.track.style.scrollBehavior;
      const previousSnap = this.track.style.scrollSnapType;
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft += shift;
      if (this.drag) this.drag.scroll += shift;
      requestAnimationFrame(() => {
        this.track.style.scrollBehavior = previousBehavior;
        this.track.style.scrollSnapType = previousSnap;
        this.rebalancing = false;
      });
      return true;
    }

    goToLineup(nextIndex, smooth = true, preferOriginal = false) {
      if (this.isFocused) return;
      const index = this.wrapIndex(nextIndex);
      const slide = preferOriginal
        ? this.slides[index]
        : this.getNearestRailSlide(index);
      if (!slide) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const left = Math.max(0, Math.min(maxScroll, slide.offsetLeft + (slide.offsetWidth / 2) - (this.track.clientWidth / 2)));
      if (smooth && !this.reducedMotion.matches) {
        this.track.scrollTo({ left, behavior: 'smooth' });
      } else {
        const previousBehavior = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = left;
        this.track.style.scrollBehavior = previousBehavior;
      }
      this.index = index;
      this.renderNeutral();
    }

    nearestIndex() {
      const target = this.track.scrollLeft + (this.track.clientWidth / 2);
      const nearest = this.railSlides.reduce((best, slide) => {
        if (!best) return slide;
        const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
        const bestCenter = best.offsetLeft + (best.offsetWidth / 2);
        return Math.abs(slideCenter - target) < Math.abs(bestCenter - target) ? slide : best;
      }, null);
      return this.wrapIndex(Number(nearest?.dataset.loopIndex || 0));
    }

    renderLineupScale() {
      if (this.isFocused || !this.track?.clientWidth) return;
      const viewportCenter = this.track.scrollLeft + (this.track.clientWidth / 2);
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const maximumScale = mobile ? 1.12 : 1.16;
      const growthRange = Math.max(
        this.track.clientWidth * (mobile ? .42 : .34),
        (this.railSlides[0]?.offsetWidth || 1) * 1.35,
      );

      this.railSlides.forEach((slide) => {
        const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
        const proximity = Math.max(0, 1 - (Math.abs(slideCenter - viewportCenter) / growthRange));
        const eased = proximity * proximity * (3 - (2 * proximity));
        const scale = 1 + ((maximumScale - 1) * eased);
        slide.style.setProperty('--br-lookbook-scale', scale.toFixed(4));
      });
    }

    renderNeutral() {
      this.railSlides.forEach((slide) => {
        slide.classList.remove('is-current', 'is-active', 'is-entering-next', 'is-entering-prev', 'is-leaving-next', 'is-leaving-prev');
      });
      this.getNearestRailSlide(this.index)?.classList.add('is-current');
      this.renderLineupScale();
      this.pages.forEach((page, index) => page.setAttribute('aria-current', String(index === this.index)));
      if (this.counter) this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      if (this.prev) this.prev.disabled = this.slides.length < 2;
      if (this.next) this.next.disabled = this.slides.length < 2;
      if (this.mode) this.mode.textContent = 'Collection index';
      if (this.modeCopy) this.modeCopy.textContent = 'Drag / swipe';
    }

    setFocusedChrome(index) {
      this.index = this.wrapIndex(index);
      if (this.prev) this.prev.disabled = this.slides.length < 2;
      if (this.next) this.next.disabled = this.slides.length < 2;
      this.pages.forEach((page, pageIndex) => page.setAttribute('aria-current', String(pageIndex === this.index)));
      this.focusLinks.forEach((link, linkIndex) => link.setAttribute('aria-expanded', String(linkIndex === this.index)));
      if (this.counter) {
        this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      }
      if (this.mode) this.mode.textContent = 'Focused record';
      if (this.modeCopy) this.modeCopy.textContent = 'Swipe / controls for next record';
    }

    bindFocus() {
      this.focusLinks.forEach((link, index) => {
        link.addEventListener('click', (event) => {
          if (this.suppressClick) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          this.enterFocus(index);
        });
      });

      this.querySelectorAll('[data-br-lookbook-back]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.exitFocus();
        });
      });
    }

    async enterFocus(index, sourceSlide = null) {
      if (this.transitioning || this.isFocused) return;
      const nextIndex = this.wrapIndex(index);
      const slide = this.slides[nextIndex];
      const figure = slide?.querySelector('.br-lookbook__figure');
      const sourceFigure = sourceSlide?.querySelector('.br-lookbook__figure') || figure;
      const panel = this.panels[nextIndex];
      if (!slide || !figure || !sourceFigure || !panel) return;

      this.transitioning = true;
      this.isFocused = true;
      this.index = nextIndex;
      const startRect = sourceFigure.getBoundingClientRect();
      const flight = this.createFlight(sourceFigure, startRect);

      this.renderNeutral();
      slide.classList.add('is-active');
      figure.classList.add('is-transition-source');
      panel.hidden = false;
      panel.classList.add('is-panel-preparing');
      this.classList.add('is-focused', 'is-focusing');
      this.setFocusedChrome(nextIndex);

      await this.nextFrame();
      this.syncPanelPeek(panel);
      const endRect = figure.getBoundingClientRect();
      await this.animateFlight(flight, startRect, endRect);

      flight?.remove();
      this.flight = null;
      figure.classList.remove('is-transition-source');
      panel.classList.remove('is-panel-preparing');
      panel.classList.add('is-panel-entering');
      this.classList.remove('is-focusing');
      window.setTimeout(() => panel.classList.remove('is-panel-entering'), 420);
      this.transitioning = false;
    }

    createFlight(figure, rect) {
      const image = figure.querySelector('img');
      if (!image || this.reducedMotion.matches) return null;
      const flight = document.createElement('div');
      const clone = image.cloneNode(true);
      clone.removeAttribute('loading');
      clone.removeAttribute('fetchpriority');
      flight.className = 'br-lookbook__flight';
      flight.append(clone);
      Object.assign(flight.style, {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      });
      document.body.append(flight);
      this.flight = flight;
      return flight;
    }

    async animateFlight(flight, startRect, endRect) {
      if (!flight || this.reducedMotion.matches) return;
      const animation = flight.animate([
        { top: `${startRect.top}px`, left: `${startRect.left}px`, width: `${startRect.width}px`, height: `${startRect.height}px`, filter: 'brightness(1)' },
        { top: `${endRect.top}px`, left: `${endRect.left}px`, width: `${endRect.width}px`, height: `${endRect.height}px`, filter: 'brightness(1.08)' },
      ], {
        duration: 620,
        easing: 'cubic-bezier(.2,.82,.2,1)',
        fill: 'forwards',
      });
      await animation.finished.catch(() => {});
    }

    async navigateFocused(nextIndex) {
      const index = this.wrapIndex(nextIndex);
      if (!this.isFocused || this.transitioning || index === this.index) return;
      const oldIndex = this.index;
      const forwardDistance = (index - oldIndex + this.slides.length) % this.slides.length;
      const backwardDistance = (oldIndex - index + this.slides.length) % this.slides.length;
      const direction = forwardDistance <= backwardDistance ? 'next' : 'prev';
      const oldSlide = this.slides[oldIndex];
      const nextSlide = this.slides[index];
      const oldPanel = this.panels[oldIndex];
      const nextPanel = this.panels[index];
      if (!oldSlide || !nextSlide || !nextPanel) return;

      this.transitioning = true;
      nextPanel.hidden = false;
      nextSlide.classList.add('is-active', `is-entering-${direction}`);
      oldSlide.classList.add(`is-leaving-${direction}`);
      this.setFocusedChrome(index);
      this.syncPanelPeek(nextPanel);

      await this.delay(this.reducedMotion.matches ? 0 : 620);
      oldSlide.classList.remove('is-active', `is-leaving-${direction}`);
      nextSlide.classList.remove(`is-entering-${direction}`);
      if (oldPanel) oldPanel.hidden = true;
      this.transitioning = false;
    }

    syncPanelPeek(panel) {
      if (!panel) return;
      if (!window.matchMedia('(max-width: 749px)').matches) {
        panel.style.removeProperty('--br-lookbook-panel-peek');
        return;
      }
      const title = panel.querySelector(':scope > h2');
      if (!title) return;
      const panelRect = panel.getBoundingClientRect();
      const titleRect = title.getBoundingClientRect();
      const previewBar = document.querySelector('#PBarNextFrame');
      const reservedBottom = previewBar ? Math.min(64, innerHeight * .1) : 0;
      const peek = Math.ceil(titleRect.bottom - panelRect.top + reservedBottom + 2);
      panel.style.setProperty('--br-lookbook-panel-peek', `${peek}px`);
    }

    async exitFocus() {
      if (!this.isFocused || this.transitioning) return;
      const activeIndex = this.index;
      const activePanel = this.panels[activeIndex];
      this.transitioning = true;
      this.classList.add('is-closing');
      activePanel?.classList.add('is-panel-leaving');
      await this.delay(this.reducedMotion.matches ? 0 : 280);

      const top = window.scrollY + this.getBoundingClientRect().top;
      this.isFocused = false;
      this.classList.remove('is-focused', 'is-focusing', 'is-closing');
      activePanel?.classList.remove('is-panel-leaving');
      this.renderNeutral();
      this.goToLineup(activeIndex, false);
      window.scrollTo({ top, behavior: 'auto' });
      this.transitioning = false;
      this.focusLinks[activeIndex]?.focus({ preventScroll: true });
    }

    nextFrame() {
      return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    delay(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }

    bindVariants() {
      this.querySelectorAll('[data-br-lookbook-form]').forEach((form) => {
        form.querySelectorAll('[data-br-lookbook-choice]').forEach((choice) => {
          choice.addEventListener('click', () => {
            const group = choice.closest('[data-br-lookbook-option]');
            group?.querySelectorAll('[data-br-lookbook-choice]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
            choice.setAttribute('aria-pressed', 'true');
            const label = group?.querySelector('[data-br-lookbook-selected]');
            if (label) label.textContent = choice.dataset.value || choice.textContent.trim();
            this.updateVariant(form);
          });
        });
      });
    }

    updateVariant(form) {
      const slide = form.closest('[data-br-lookbook-slide]');
      const json = slide?.querySelector('[data-br-lookbook-variants]');
      if (!json) return;

      let variants;
      try {
        variants = JSON.parse(json.textContent);
      } catch (_) {
        return;
      }

      const selected = [...form.querySelectorAll('[data-br-lookbook-option]')].map((group) => {
        return group.querySelector('[data-br-lookbook-choice][aria-pressed="true"]')?.dataset.value;
      });
      const variant = variants.find((entry) => entry.options.every((value, index) => value === selected[index]));
      const input = form.querySelector('[data-br-lookbook-variant-id]');
      const add = form.querySelector('[data-br-lookbook-add]');
      const prices = slide.querySelectorAll('[data-br-lookbook-price]');
      const compares = slide.querySelectorAll('[data-br-lookbook-compare]');

      if (!variant) {
        if (input) input.value = '';
        if (add) {
          add.disabled = true;
          add.querySelector('span').textContent = add.dataset.unavailableText || 'Unavailable';
        }
        return;
      }

      if (input) input.value = variant.id;
      prices.forEach((price) => { price.textContent = this.money.format(variant.price / 100); });
      compares.forEach((compare) => {
        compare.textContent = variant.compare_at_price > variant.price ? this.money.format(variant.compare_at_price / 100) : '';
        compare.hidden = !compare.textContent;
      });
      if (add) {
        add.disabled = !variant.available;
        add.querySelector('span').textContent = variant.available
          ? (add.dataset.addText || 'Add to cart')
          : (add.dataset.soldOutText || 'Sold out');
      }
    }
  }

  customElements.define('br-lookbook', BrLookbook);
}